import {PhoneHQGlyph} from 'ch3-ui-lib';
import React, { useState, useRef, useEffect } from 'react';
import PlaybackSpeedButton from './PlaybackSpeedButton.tsx';
import {usePlaybackSpeed} from '../../hooks/usePlaybackSpeed.ts';
import {formatMinutes, formatSeconds} from "../../utilis/timeUtilis.ts";
import {playMono} from './monoPlayback';
import {Download} from 'lucide-react'

interface AudioPlayerProps {
    src: string;
    disabled?: boolean;
    filename?: string;
    className?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, filename, className }) => {
    const [hideIcon, setHideIcon] = useState(false);
    const [audioCurrentMinutes, setAudioCurrentMinutes] = useState('00');
    const [audioCurrentSeconds, setAudioCurrentSeconds] = useState('00');
    const [currentProgressValue, setCurrentProgressValue] = useState(0);
    const audioRef = useRef<HTMLAudioElement>(null);
    const trackLineRef = useRef<HTMLInputElement>(null);
    const {rate, cycleRate} = usePlaybackSpeed(audioRef, src);

    useEffect(() => {
        resetAudio();
    }, [src]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
    }, []);

    const handleMetadataLoaded = () => {
        if (audioRef.current) {
            setAudioCurrentMinutes(formatMinutes(audioRef.current.duration));
            setAudioCurrentSeconds(formatSeconds(audioRef.current.duration));
        }
    };

    const resetAudio = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            setCurrentProgressValue(0);
            updateTimeline();
            pause();
        }
    };

    const play = () => {
        if (audioRef.current) {
            if (audioRef.current.readyState < 4) return;
            void playMono(audioRef.current);
            setHideIcon(true);
        }
    };

    const pause = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setHideIcon(false);
        }
    };

    const currentProgress = () => {
        if (audioRef.current && audioRef.current.duration) {
            const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
            return progress;
        }
        return 0;
    }

    const updateTimeline = () => {
        if (audioRef.current && trackLineRef.current) {
            trackLineRef.current.max = audioRef.current.duration.toString();
            trackLineRef.current.value = audioRef.current.currentTime.toString();
            setAudioCurrentMinutes(formatMinutes(audioRef.current.duration - audioRef.current.currentTime));
            setAudioCurrentSeconds(formatSeconds(audioRef.current.duration - audioRef.current.currentTime));
            setCurrentProgressValue(currentProgress());
        }
    };

    const updateAudio = () => {
        if (audioRef.current && trackLineRef.current) {
            updateTimeline();
        }
    };

    const endAudio = () => {
        pause();
        resetAudio();
    };

    const handleTimelineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (audioRef.current && trackLineRef.current) {
            const newTime = parseFloat(e.target.value);
            audioRef.current.currentTime = newTime;
            const newProgress = (newTime / audioRef.current.duration) * 100;
            setCurrentProgressValue(newProgress);
            updateTimeline();
        }
    };

    return (
        src &&
        <div onClick={event => event.stopPropagation()} className={`player-wrapper py-3 bg-transparent ${className}`}>
            <div className="player-content text-ink flex gap-3 items-center ">
                <div className="controls rounded-full bg-brand min-h-8 min-w-8 flex justify-center items-center">
                    <button type="button" aria-label="Pause" onClick={pause} className={`player-icon flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-white ${!hideIcon ? 'hidden' : ''}`}><PhoneHQGlyph name="pause" size={24}/></button>
                    <button type="button" aria-label="Play" onClick={play} className={`player-icon flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-white ${hideIcon ? 'hidden' : ''}`}><PhoneHQGlyph name="play_arrow" size={24}/></button>
                </div>
                <PlaybackSpeedButton rate={rate} onClick={cycleRate} shape="round" />
                <div className="timeline w-full">
                    <input
                        ref={trackLineRef}
                        type="range"
                        min="0"
                        max="100"
                        step="0.01"
                        value={audioRef.current?.currentTime || 0}
                        className="timeline-input w-full h-1 bg-brand appearance-none"
                        onChange={handleTimelineChange}
                        style={{
                            background: `linear-gradient(to right, #6133EA ${currentProgressValue}%, #DFE0E2 ${currentProgressValue}%)`
                        }}
                    />
                </div>
                <div className="controls-wrapper flex justify-between items-center">
                    <div className="time text-ink min-w-8">
                        <span className='text-xs font-medium'>{audioCurrentMinutes}:{audioCurrentSeconds}</span>
                    </div>
                </div>
                <div>
                    <Download onClick={() => {
                        if (audioRef.current) {

                        const link = document.createElement('a');
                        link.href = audioRef.current.src;
                        link.download = filename + '.mp3' || 'audio-file.mp3';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                    }} strokeWidth={2} size={20} className={'text-brand-ink cursor-pointer'} />
                </div>
            </div>
            <audio
                ref={audioRef}
                src={src}
                onTimeUpdate={updateAudio}
                onEnded={endAudio}
                onPause={() => setHideIcon(false)}
                onLoadedMetadata={handleMetadataLoaded}
                className="hidden"
            />
        </div>
    );
};

export default AudioPlayer;